// useNodesByKeysQuery.js

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { nodeQueryKeys, normalizeNodeKeysForQuery } from 'hooks/nodeQueryKeys';
import { getNodesByKeys } from 'services/nodes/NodeReadService';

const DEFAULT_STALE_TIME_MS = 30_000;
const DEFAULT_GC_TIME_MS = 300_000;
const EMPTY_NODES = [];

/**
 * useNodesByKeysQuery
 *
 * Cached batch fetch for node documents by key.
 *
 * @param {string[]} nodeKeys - Node keys to load.
 * @param {object} [options] - Query options.
 * @param {boolean} [options.enabled=true] - Whether the query should run.
 * @param {number} [options.staleTime=30000] - Cache freshness window in ms.
 * @param {number} [options.gcTime=300000] - Garbage collection window in ms.
 * @param {string} [options.visibility] - Optional NodeReadService visibility filter.
 *
 * @returns {{ nodes: object[], nodesByKey: Map<string, object>, isLoading: boolean, isFetching: boolean, error: Error|null }}
 */
export default function useNodesByKeysQuery(nodeKeys = [], options = {}) {
  const {
    enabled = true,
    staleTime = DEFAULT_STALE_TIME_MS,
    gcTime = DEFAULT_GC_TIME_MS,
    visibility,
  } = options;

  const normalizedKeys = useMemo(
    () => normalizeNodeKeysForQuery(nodeKeys),
    [nodeKeys],
  );

  const queryKey = useMemo(
    () => nodeQueryKeys.byKeys(normalizedKeys),
    [normalizedKeys],
  );

  const query = useQuery({
    queryKey,
    queryFn: () => getNodesByKeys(
      normalizedKeys,
      visibility ? { visibility } : {},
    ),
    enabled: enabled && normalizedKeys.length > 0,
    staleTime,
    gcTime,
  });

  const nodes = Array.isArray(query.data) ? query.data : EMPTY_NODES;

  const nodesByKey = useMemo(() => {
    const map = new Map();
    nodes.forEach((node) => {
      const key = String(node?.key || '').trim();
      if (key) {
        map.set(key, node);
      }
    });
    return map;
  }, [nodes]);

  return {
    nodes,
    nodesByKey,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error || null,
  };
}
